import React, { useState, useEffect } from 'react';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';

/**
 * LevelDiagnosticsPanel — content-only kit tool (`levelDiag`). Read-only
 * level/geometry/spawn inspector that polls MapSystem.getCurrentLevel() @1Hz.
 *
 * Migrated off the UIContext `showLevelDiagnostics` flag into the 🛠 debug-tools
 * kit (dev-tools modernization arc, 2026-05-29). The kit's ToolWindow owns the
 * frame + close button, so this renders content only (no fixed modal chrome).
 */
export const LevelDiagnosticsPanel: React.FC = () => {
  const [levelInfo, setLevelInfo] = useState<any>(null);
  const [spawnPoints, setSpawnPoints] = useState<any[]>([]);
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    const updateLevelInfo = () => {
      const currentLevel = ServiceRegistry.getInstance().get('MapSystem')?.getCurrentLevel();
      if (currentLevel) {
        setLevelInfo({
          name: currentLevel.name,
          id: currentLevel.id,
          width: currentLevel.width,
          height: currentLevel.height,
          backgroundColor: currentLevel.backgroundColor,
          platformCount: currentLevel.platforms.length,
          spawnPointCount: currentLevel.spawnPoints.length,
        });
        setSpawnPoints(currentLevel.spawnPoints || []);
        setPlatforms(currentLevel.platforms || []);
        setLastUpdate(new Date());
      }
    };
    updateLevelInfo();
    const interval = setInterval(updateLevelInfo, 1000);
    return () => clearInterval(interval);
  }, []);

  if (!levelInfo) {
    return (
      <div style={{ padding: 12, color: '#ff6666', fontSize: 12 }}>
        ❌ No level loaded. Make sure you&apos;re in a game scene.
      </div>
    );
  }

  return (
    <div style={{ padding: 12, color: 'white', overflow: 'auto', height: '100%', fontSize: 12 }}>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ color: '#ffff00', margin: '0 0 8px' }}>Level Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><strong>Name:</strong> {levelInfo.name}</div>
          <div><strong>ID:</strong> {levelInfo.id}</div>
          <div><strong>Dimensions:</strong> {levelInfo.width} × {levelInfo.height}</div>
          <div><strong>Background:</strong> {levelInfo.backgroundColor}</div>
          <div><strong>Platforms:</strong> {levelInfo.platformCount}</div>
          <div><strong>Spawn Points:</strong> {levelInfo.spawnPointCount}</div>
          <div><strong>Last Update:</strong> {lastUpdate.toLocaleTimeString()}</div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <h3 style={{ color: '#ffff00', margin: '0 0 8px' }}>Spawn Points</h3>
        {spawnPoints.length > 0 ? (
          <div style={{ maxHeight: 150, overflowY: 'auto' }}>
            {spawnPoints.map((point, index) => (
              <div key={index} style={{ padding: 6, marginBottom: 4, background: 'rgba(0,255,0,0.1)', border: '1px solid #00ff00', borderRadius: 4 }}>
                <strong>Spawn {index + 1}:</strong> ({point.x}, {point.y})
                {point.name && <span> - {point.name}</span>}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#ff6666' }}>❌ No spawn points defined!</div>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <h3 style={{ color: '#ffff00', margin: '0 0 8px' }}>Platforms</h3>
        {platforms.length > 0 ? (
          <div style={{ maxHeight: 150, overflowY: 'auto' }}>
            {platforms.slice(0, 10).map((platform, index) => (
              <div key={index} style={{ padding: 6, marginBottom: 4, background: 'rgba(0,100,255,0.1)', border: '1px solid #4444ff', borderRadius: 4 }}>
                <strong>Platform {index + 1}:</strong> ({platform.x}, {platform.y}) - {platform.width}×{platform.height}
                {platform.type && <span> - {platform.type}</span>}
              </div>
            ))}
            {platforms.length > 10 && (
              <div style={{ color: '#888', fontStyle: 'italic' }}>... and {platforms.length - 10} more</div>
            )}
          </div>
        ) : (
          <div style={{ color: '#ff6666' }}>❌ No platforms defined!</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button
          onClick={() => {
            gameLogger.debug('debug', '[LevelDiagnostics] level', levelInfo);
            gameLogger.debug('debug', '[LevelDiagnostics] spawns', spawnPoints);
            gameLogger.debug('debug', '[LevelDiagnostics] platforms', platforms);
            gameLogger.info('debug', 'Level details logged to console');
          }}
          style={{ background: '#4444ff', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}
        >
          Log to Console
        </button>
        <button
          onClick={() => {
            const exportData = { timestamp: new Date().toISOString(), levelInfo, spawnPoints, platforms: platforms.slice(0, 20) };
            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `level-diagnostics-${levelInfo.name}-${Date.now()}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          style={{ background: '#44aa44', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}
        >
          Export Data
        </button>
      </div>

      <div style={{ background: 'rgba(255,255,0,0.1)', border: '1px solid #ffff00', borderRadius: 4, padding: 8 }}>
        <strong>💡 Tips:</strong>
        <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
          <li>Check spawn points are defined for proper player positioning</li>
          <li>Verify platforms are within level bounds</li>
          <li>Use DevTools to switch levels and test spawning</li>
        </ul>
      </div>
    </div>
  );
};
